import React, { useState, useRef } from 'react';
import { Loader2, AlertCircle, CheckCircle, ChevronLeft } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../../lib/supabase';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface ForgotPasswordProps {
  onBack: () => void;
}

export function ForgotPassword({ onBack }: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Send reset email through Supabase's built-in system
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
        ...(TURNSTILE_SITE_KEY && captchaToken ? { captchaToken } : {}),
      });

      if (resetError) throw resetError;

      // Always show success message for security (don't reveal if email exists)
      setResetSent(true);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
      setCaptchaToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-midnight mb-2">Reset Password</h2>
        <p className="text-slate">
          Enter your email address and we'll send you instructions to reset your password.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-crimson/10 text-crimson rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {resetSent ? (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald/10 text-emerald rounded-full mb-4">
            <CheckCircle className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-semibold text-midnight mb-2">Check Your Email</h3>
          <p className="text-slate mb-6">
            We've sent password reset instructions to {email}
          </p>
          <button
            type="button"
            onClick={onBack}
            className="text-electric hover:text-electric/80"
          >
            Return to Sign In
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              required
            />
          </div>

          {TURNSTILE_SITE_KEY && (
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setCaptchaToken}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
              options={{ theme: 'light', size: 'normal' }}
            />
          )}

          <div className="flex justify-between items-center pt-4">
            <button
              type="button"
              onClick={onBack}
              className="text-slate hover:text-midnight flex items-center"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </button>

            <button
              type="submit"
              disabled={loading || !email || (!!TURNSTILE_SITE_KEY && !captchaToken)}
              className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 min-w-[120px]"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              ) : (
                'Send Reset Link'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}